import { type FC, useMemo } from 'react';
import type { YearSales } from '../../../data/year-sales';
import { formatDisplayValue, getDisplayValue } from '../helpers';
import type { DisplayType } from '../types';
import { formatPercentYoY } from '.';

type HistoricalYearsTooltipProps = {
  historicalYears: YearSales[];
  displayType: DisplayType;
};

export const HistoricalYearsTooltip: FC<HistoricalYearsTooltipProps> = ({
  historicalYears,
  displayType,
}) => {
  const rows = useMemo(() => {
    return historicalYears.map((yearSales, index) => {
      const value = getDisplayValue(yearSales.totals, displayType);

      // Short-circuit if there's no previous year to compare
      if (index >= historicalYears.length - 1) {
        return {
          year: yearSales.year,
          value,
          yoyChange: undefined,
        };
      }

      const previousValue = getDisplayValue(historicalYears[index + 1].totals, displayType);

      const yoyChange =
        value !== undefined && previousValue !== undefined && previousValue !== 0
          ? (value - previousValue) / previousValue
          : undefined;

      return {
        year: yearSales.year,
        value,
        yoyChange,
      };
    });
  }, [historicalYears, displayType]);

  return (
    <div className="w-72 bg-white rounded-md shadow-xl border border-gray-200 p-3 text-sm">
      <div className="font-medium text-gray-900 mb-2 border-b pb-1">Historical Totals</div>
      <div className="flex flex-col divide-y divide-gray-200">
        <div className="flex flex-row pb-1">
          <div className="basis-1/3 flex-none text-gray-500 font-medium">Year</div>
          <div className="basis-1/3 flex-none text-gray-500 font-medium">Total</div>
          <div className="basis-1/3 flex-none text-gray-500 font-medium text-right">YoY Change</div>
        </div>

        {rows.map((row) => (
          <div key={row.year} className="flex flex-row p-1">
            <div className="basis-1/3 flex-none font-semibold tabular-nums">{row.year}</div>
            <div className="basis-1/3 flex-none tabular-nums">
              {formatDisplayValue(row.value, displayType)}
            </div>
            <div className="basis-1/3 flex-none text-right tabular-nums">
              {row.yoyChange !== undefined ? (
                <span className={row.yoyChange < 0 ? 'text-red-600' : 'text-green-600'}>
                  {formatPercentYoY(row.yoyChange)}
                </span>
              ) : (
                <span className="text-gray-400">-</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
